import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { ArrowRight, DollarSign, Users, Gauge, Loader2, Zap, Brain, Target } from 'lucide-react';
import { Button } from '../components/ui/button';

const STEPS = 4;

const Onboarding = () => {
  const navigate = useNavigate();
  const { api, setUser, user } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [animateIn, setAnimateIn] = useState(true);
  const [formData, setFormData] = useState({
    cash_flow: '',
    monthly_revenue: '',
    team_size: '',
    pressure_level: '',
    top_challenge: '',
  });
  const [atlasResponse, setAtlasResponse] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (user?.onboarded) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const transition = (nextStep) => {
    setAnimateIn(false);
    setTimeout(() => {
      setStep(nextStep);
      setAnimateIn(true);
    }, 300);
  };

  const handleSubmitOnboarding = async () => {
    setAnalyzing(true);
    transition(3);

    try {
      const payload = {
        cash_flow: formData.cash_flow ? parseFloat(formData.cash_flow) : null,
        monthly_revenue: formData.monthly_revenue ? parseFloat(formData.monthly_revenue) : null,
        team_size: formData.team_size ? parseInt(formData.team_size) : null,
        pressure_level: formData.pressure_level || null,
        top_challenge: formData.top_challenge || null,
      };

      const res = await api.post('/api/onboarding', payload);

      await new Promise(resolve => setTimeout(resolve, 1800));

      setAtlasResponse(res.data.atlas_response);
      setAnalyzing(false);

      setUser(prev => ({ ...prev, onboarded: true }));
    } catch (error) {
      console.error('Onboarding error:', error);
      setAnalyzing(false);
      setAtlasResponse(t('onboarding.step4.analyzing'));
    }
  };

  const finishOnboarding = () => {
    navigate('/dashboard');
  };

  const pressureLevels = ['low', 'moderate', 'high', 'critical'];
  const pressureColors = {
    low: 'border-green-500/50 bg-green-500/10 text-green-400',
    moderate: 'border-atlasYellow/50 bg-atlasYellow/10 text-atlasYellow',
    high: 'border-orange-500/50 bg-orange-500/10 text-orange-400',
    critical: 'border-atlasRed/50 bg-atlasRed/10 text-atlasRed',
  };

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-atlasBg flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-atlasBg via-atlasCard/20 to-atlasBg" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-atlasCyan/5 rounded-full blur-3xl" />

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-atlasBorder z-50">
        <div
          className="h-full bg-atlasCyan transition-all duration-700 ease-out"
          style={{ width: `${((step + 1) / STEPS) * 100}%` }}
        />
      </div>

      <div className={`relative z-10 w-full max-w-xl mx-auto px-6 transition-all duration-300 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>

        {/* Step 0: Welcome to ATLAS */}
        {step === 0 && (
          <div data-testid="onboarding-step-welcome" className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-atlasCyan/10 mb-8 ring-2 ring-atlasCyan/20">
              <Brain className="w-10 h-10 text-atlasCyan" />
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
              {t('onboarding.welcome.title') || 'Bienvenue dans ATLAS'}
            </h1>
            <p className="text-lg text-atlasTextSecondary mb-10 leading-relaxed max-w-md mx-auto">
              {t('onboarding.welcome.subtitle') || 'Votre copilote stratégique est prêt. Prenons 2 minutes pour calibrer ATLAS à votre activité.'}
            </p>

            {/* What ATLAS will do */}
            <div className="grid gap-4 mb-10 text-left max-w-sm mx-auto">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-atlasCyan/10 rounded-lg flex-shrink-0 mt-0.5">
                  <Target className="w-4 h-4 text-atlasCyan" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{t('onboarding.welcome.feature1Title') || 'Analyse stratégique'}</p>
                  <p className="text-atlasTextSecondary text-xs mt-0.5">{t('onboarding.welcome.feature1Desc') || 'ATLAS analyse votre situation et propose des actions concrètes'}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-atlasPurple/10 rounded-lg flex-shrink-0 mt-0.5">
                  <Zap className="w-4 h-4 text-atlasPurple" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{t('onboarding.welcome.feature2Title') || 'Décisions rapides'}</p>
                  <p className="text-atlasTextSecondary text-xs mt-0.5">{t('onboarding.welcome.feature2Desc') || 'Chaque session se termine par une décision claire à prendre'}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-atlasYellow/10 rounded-lg flex-shrink-0 mt-0.5">
                  <DollarSign className="w-4 h-4 text-atlasYellow" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{t('onboarding.welcome.feature3Title') || 'Focus croissance'}</p>
                  <p className="text-atlasTextSecondary text-xs mt-0.5">{t('onboarding.welcome.feature3Desc') || 'Suivi de vos KPIs, cash-flow et priorités en temps réel'}</p>
                </div>
              </div>
            </div>

            <Button
              data-testid="onboarding-start-btn"
              onClick={() => transition(1)}
              className="btn-primary px-8 py-4 text-lg flex items-center gap-3 mx-auto"
            >
              {t('onboarding.welcome.cta') || 'Configurer ATLAS'}
              <ArrowRight className="w-5 h-5" />
            </Button>
            <p className="text-atlasTextSecondary/40 text-xs mt-4">{t('onboarding.welcome.time') || '~ 2 minutes'}</p>
          </div>
        )}

        {/* Step 1: Business Snapshot */}
        {step === 1 && (
          <div data-testid="onboarding-step-2">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">
              {t('onboarding.step2.title')}
            </h2>

            <div className="space-y-5">
              <div className="glass-card p-4 flex items-center gap-4">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div className="flex-1">
                  <label className="text-sm text-atlasTextSecondary block mb-1">{t('onboarding.step2.cashFlow')}</label>
                  <input
                    data-testid="onboarding-cashflow-input"
                    type="number"
                    value={formData.cash_flow}
                    onChange={(e) => setFormData(prev => ({ ...prev, cash_flow: e.target.value }))}
                    className="w-full bg-transparent border-none outline-none text-white text-xl atlas-mono placeholder-atlasTextSecondary/50"
                    placeholder="150 000"
                  />
                </div>
              </div>

              <div className="glass-card p-4 flex items-center gap-4">
                <div className="p-3 bg-atlasCyan/10 rounded-lg">
                  <DollarSign className="w-5 h-5 text-atlasCyan" />
                </div>
                <div className="flex-1">
                  <label className="text-sm text-atlasTextSecondary block mb-1">{t('onboarding.step2.revenue')}</label>
                  <input
                    data-testid="onboarding-revenue-input"
                    type="number"
                    value={formData.monthly_revenue}
                    onChange={(e) => setFormData(prev => ({ ...prev, monthly_revenue: e.target.value }))}
                    className="w-full bg-transparent border-none outline-none text-white text-xl atlas-mono placeholder-atlasTextSecondary/50"
                    placeholder="50 000"
                  />
                </div>
              </div>

              <div className="glass-card p-4 flex items-center gap-4">
                <div className="p-3 bg-atlasPurple/10 rounded-lg">
                  <Users className="w-5 h-5 text-atlasPurple" />
                </div>
                <div className="flex-1">
                  <label className="text-sm text-atlasTextSecondary block mb-1">{t('onboarding.step2.teamSize')}</label>
                  <input
                    data-testid="onboarding-team-input"
                    type="number"
                    value={formData.team_size}
                    onChange={(e) => setFormData(prev => ({ ...prev, team_size: e.target.value }))}
                    className="w-full bg-transparent border-none outline-none text-white text-xl atlas-mono placeholder-atlasTextSecondary/50"
                    placeholder="8"
                  />
                </div>
              </div>

              {/* Pressure Level */}
              <div>
                <p className="text-white font-medium mb-3 flex items-center gap-2">
                  <Gauge className="w-5 h-5 text-atlasTextSecondary" />
                  {t('onboarding.step2.pressure')}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {pressureLevels.map((level) => (
                    <button
                      key={level}
                      data-testid={`pressure-${level}-btn`}
                      onClick={() => setFormData(prev => ({ ...prev, pressure_level: level }))}
                      className={`p-3 rounded-lg border text-sm transition-all ${
                        formData.pressure_level === level
                          ? pressureColors[level]
                          : 'border-atlasBorder text-atlasTextSecondary hover:border-white/20'
                      }`}
                    >
                      {t(`onboarding.step2.pressureLevels.${level}`)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <Button
                data-testid="onboarding-next-btn"
                onClick={() => transition(2)}
                className="btn-primary px-8 py-3 flex items-center gap-2"
              >
                {t('onboarding.step2.cta')}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Priority Challenge */}
        {step === 2 && (
          <div data-testid="onboarding-step-3" className="text-center">
            <h2 className="text-2xl font-bold text-white mb-8">
              {t('onboarding.step3.title')}
            </h2>

            <textarea
              ref={textareaRef}
              data-testid="onboarding-challenge-input"
              value={formData.top_challenge}
              onChange={(e) => setFormData(prev => ({ ...prev, top_challenge: e.target.value }))}
              className="w-full glass-card p-5 bg-transparent text-white text-lg leading-relaxed resize-none outline-none placeholder-atlasTextSecondary/50 min-h-[140px]"
              placeholder={t('onboarding.step3.placeholder')}
              autoFocus
            />

            <div className="mt-8">
              <Button
                data-testid="onboarding-analyze-btn"
                onClick={handleSubmitOnboarding}
                disabled={!formData.top_challenge?.trim()}
                className="btn-primary px-8 py-3 text-lg flex items-center gap-2 mx-auto disabled:opacity-40"
              >
                {t('onboarding.step3.cta')}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: ATLAS Response */}
        {step === 3 && (
          <div data-testid="onboarding-step-4">
            {analyzing ? (
              <div className="text-center py-12">
                <div className="relative mx-auto w-16 h-16 mb-6">
                  <div className="absolute inset-0 rounded-full border-2 border-atlasCyan/20" />
                  <div className="absolute inset-0 rounded-full border-2 border-atlasCyan border-t-transparent animate-spin" />
                </div>
                <p className="text-lg text-atlasCyan atlas-mono animate-pulse">
                  {t('onboarding.step4.analyzing')}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center gap-2">
                  <span className="atlas-brand text-atlasCyan tracking-wider">ATLAS</span>
                </div>
                <div className="glass-card p-6 glow-cyan mb-8">
                  <div className="text-white leading-relaxed whitespace-pre-wrap text-[15px]" data-testid="atlas-onboarding-response">
                    {atlasResponse.split('\n').map((line, i) => {
                      if (line.startsWith('**') && line.endsWith('**')) {
                        return <p key={i} className="font-bold text-atlasCyan mt-4 mb-1">{line.replace(/\*\*/g, '')}</p>;
                      }
                      if (line.match(/^\d+\.\s\*\*/)) {
                        const cleaned = line.replace(/\*\*/g, '');
                        const colonIdx = cleaned.indexOf('\u2014') !== -1 ? cleaned.indexOf('\u2014') : cleaned.indexOf(':');
                        if (colonIdx > 0) {
                          return (
                            <p key={i} className="mt-4 mb-1">
                              <span className="font-bold text-atlasCyan">{cleaned.slice(0, colonIdx)}</span>
                              <span>{cleaned.slice(colonIdx)}</span>
                            </p>
                          );
                        }
                        return <p key={i} className="font-bold text-atlasCyan mt-4 mb-1">{cleaned}</p>;
                      }
                      if (line.trim() === '') return <br key={i} />;
                      return <p key={i} className="mb-1">{line.replace(/\*\*/g, '')}</p>;
                    })}
                  </div>
                </div>
                <div className="text-center">
                  <Button
                    data-testid="onboarding-finish-btn"
                    onClick={finishOnboarding}
                    className="btn-primary px-10 py-4 text-lg flex items-center gap-3 mx-auto"
                  >
                    {t('onboarding.step4.cta')}
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
